import { useContext, useCallback, useRef, useState } from 'react';

import { API_BASE_URL, API_ENDPOINTS, getHeaders, IS_DEBUG } from '../config/api';
import AuthContext from '../store/auth-context';

import {
  OCRExtractRequest,
  OCRExtractResponse,
  OCRResult,
  OCRQueuePayload,
  OCRBatchSyncRequest,
  OCRBatchSyncResponse,
  ExtractedText,
  OCRUIState,
  OCRProcessingState,
  DEFAULT_OCR_CONFIG,
  OCRCaptureConfig,
} from '../types/ocr';

import {
  captureVideoFrame,
  cropToCenter,
  compressImageToBase64,
  preprocessForOCR,
} from '../utils/image-utils';

import { findBarcodeMatches } from '../utils/barcode-matcher';

// ===========================================
// OCR Offline Queue Management
// ===========================================

const OCR_QUEUE_KEY = 'logistics_ocr_queue';

const getOCRQueue = (): OCRQueuePayload[] => {
  const stored = localStorage.getItem(OCR_QUEUE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const saveOCRQueue = (queue: OCRQueuePayload[]) => {
  localStorage.setItem(OCR_QUEUE_KEY, JSON.stringify(queue));
};

/**
 * Cleans up expired OCR queue items
 */
const cleanupExpiredOCRRequests = (expiryHours: number = 24) => {
  const queue = getOCRQueue();
  const now = Date.now();
  const expiryMs = expiryHours * 60 * 60 * 1000;

  const validQueue = queue.filter(
    (item) => now - item.capturedAt < expiryMs
  );

  if (validQueue.length !== queue.length) {
    saveOCRQueue(validQueue);
    if (IS_DEBUG) {
      console.log(
        `[OCR] Cleaned up ${queue.length - validQueue.length} expired OCR requests`
      );
    }
  }

  return validQueue;
};

// ===========================================
// Main Hook
// ===========================================

const useOCR = (config: Partial<OCRCaptureConfig> = {}) => {
  const authCtx = useContext(AuthContext);
  const syncInProgress = useRef(false);

  // Merge config with defaults
  const ocrConfig: OCRCaptureConfig = { ...DEFAULT_OCR_CONFIG, ...config };

  // UI State
  const [uiState, setUIState] = useState<OCRUIState>({
    processingState: 'idle',
    lastResult: null,
    error: null,
  });

  // Get JWT token from auth context
  const getToken = useCallback((): string | null => {
    const user = authCtx.user as any;
    return user?.jwt || null;
  }, [authCtx.user]);

  // ===========================================
  // Base Request Function
  // ===========================================

  const request = useCallback(
    async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      const token = getToken();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const url = `${API_BASE_URL}${endpoint}`;
      const headers = getHeaders(token);

      if (IS_DEBUG) {
        console.log(
          `[OCR API] ${options.method || 'GET'} ${url}`,
          options.body ? '(body omitted for brevity)' : ''
        );
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      const data = await response.json();

      if (IS_DEBUG) {
        console.log(`[OCR API] Response:`, {
          statusCode: data.statusCode,
          textsCount: data.texts?.length,
        });
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.error || `Request failed: ${response.status}`);
      }

      return data;
    },
    [getToken]
  );

  // ===========================================
  // Update UI State Helper
  // ===========================================

  const updateUIState = useCallback(
    (
      processingState: OCRProcessingState,
      lastResult?: OCRResult | null,
      error?: string | null
    ) => {
      setUIState({
        processingState,
        lastResult: lastResult ?? uiState.lastResult,
        error: error ?? null,
      });
    },
    [uiState.lastResult]
  );

  // ===========================================
  // Core OCR Functions
  // ===========================================

  /**
   * Calls the backend OCR extraction endpoint
   */
  const extractText = useCallback(
    async (
      imageBase64: string,
      expectedBarcodes?: string[]
    ): Promise<OCRExtractResponse> => {
      const payload: OCRExtractRequest = {
        imageBase64,
        expectedBarcodes,
      };

      return request<OCRExtractResponse>(API_ENDPOINTS.OCR_EXTRACT, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    [request]
  );

  /**
   * Captures a frame from video, processes it, and extracts text
   */
  const captureAndExtract = useCallback(
    async (
      video: HTMLVideoElement,
      expectedBarcodes: string[],
      boxContext?: OCRQueuePayload['boxContext']
    ): Promise<OCRResult> => {
      updateUIState('capturing');

      try {
        // Capture frame from video
        const frame = captureVideoFrame(video);
        if (!frame) {
          throw new Error('Failed to capture video frame');
        }

        // Crop to center for better focus on text
        const cropped = cropToCenter(frame, 0.7);

        // Preprocess for better OCR
        const processed = preprocessForOCR(cropped);

        // Compress to base64
        updateUIState('processing');
        const imageBase64 = await compressImageToBase64(
          processed,
          ocrConfig.jpegQuality,
          ocrConfig.maxImageSizeKB
        );

        // If offline, queue the request
        if (!navigator.onLine) {
          const queued = queueOCRRequest(imageBase64, expectedBarcodes, boxContext);
          if (queued) {
            const result: OCRResult = {
              success: false,
              matches: [],
              allExtractedTexts: [],
              fullText: '',
              imageBase64,
              timestamp: Date.now(),
              error: 'Request queued - will process when online',
            };
            updateUIState('queued', result);
            return result;
          } else {
            throw new Error('OCR queue is full. Please try again later.');
          }
        }

        // Call OCR API
        const response = await extractText(imageBase64, expectedBarcodes);

        // Find matches between OCR results and expected barcodes
        const matches = findBarcodeMatches(response.texts, expectedBarcodes);

        const result: OCRResult = {
          success: matches.length > 0,
          matches,
          allExtractedTexts: response.texts,
          fullText: response.fullText,
          timestamp: Date.now(),
        };

        updateUIState(result.success ? 'success' : 'error', result);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'OCR capture failed';
        const result: OCRResult = {
          success: false,
          matches: [],
          allExtractedTexts: [],
          fullText: '',
          timestamp: Date.now(),
          error: errorMessage,
        };
        updateUIState('error', result, errorMessage);
        return result;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extractText, ocrConfig.jpegQuality, ocrConfig.maxImageSizeKB, updateUIState]
  );

  // ===========================================
  // Offline Queue Operations
  // ===========================================

  /**
   * Queues an OCR request for later processing when offline
   */
  const queueOCRRequest = useCallback(
    (
      imageBase64: string,
      expectedBarcodes: string[],
      boxContext?: OCRQueuePayload['boxContext']
    ): boolean => {
      // Clean up expired requests first
      const queue = cleanupExpiredOCRRequests(ocrConfig.queueExpiryHours);

      // Check queue limit
      if (queue.length >= ocrConfig.maxQueuedRequests) {
        if (IS_DEBUG) {
          console.warn('[OCR] Queue full, cannot add new request');
        }
        return false;
      }

      const payload: OCRQueuePayload = {
        imageBase64,
        expectedBarcodes,
        capturedAt: Date.now(),
        boxContext,
      };

      queue.push(payload);
      saveOCRQueue(queue);

      if (IS_DEBUG) {
        console.log('[OCR] Queued OCR request:', {
          queueLength: queue.length,
          expectedBarcodes,
        });
      }

      return true;
    },
    [ocrConfig.maxQueuedRequests, ocrConfig.queueExpiryHours]
  );

  /**
   * Syncs all queued OCR requests with the backend
   */
  const syncOCRQueue = useCallback(async (): Promise<OCRBatchSyncResponse | null> => {
    if (syncInProgress.current) return null;
    if (!navigator.onLine) return null;

    const queue = cleanupExpiredOCRRequests(ocrConfig.queueExpiryHours);
    if (queue.length === 0) return null;

    syncInProgress.current = true;

    try {
      const batchRequest: OCRBatchSyncRequest = {
        requests: queue,
      };

      const response = await request<OCRBatchSyncResponse>(
        API_ENDPOINTS.OCR_BATCH_SYNC,
        {
          method: 'POST',
          body: JSON.stringify(batchRequest),
        }
      );

      // Clear successfully processed items
      // For now, clear all - the backend response indicates which succeeded
      if (response.processed > 0) {
        saveOCRQueue([]);
      }

      if (IS_DEBUG) {
        console.log('[OCR] Batch sync complete:', response);
      }

      return response;
    } catch (error) {
      if (IS_DEBUG) {
        console.error('[OCR] Batch sync failed:', error);
      }
      return null;
    } finally {
      syncInProgress.current = false;
    }
  }, [ocrConfig.queueExpiryHours, request]);

  /**
   * Gets count of queued OCR requests
   */
  const getQueuedOCRCount = useCallback((): number => {
    return getOCRQueue().length;
  }, []);

  // ===========================================
  // Utility Functions
  // ===========================================

  /**
   * Resets the UI state to idle
   */
  const resetState = useCallback(() => {
    setUIState({
      processingState: 'idle',
      lastResult: null,
      error: null,
    });
  }, []);

  /**
   * Gets the best match from the last result
   */
  const getLastBestMatch = useCallback(() => {
    if (!uiState.lastResult?.matches.length) return null;
    return uiState.lastResult.matches[0];
  }, [uiState.lastResult]);

  /**
   * Manually process extracted texts against barcodes
   * Useful for re-matching with different barcode list
   */
  const matchTextsToBarcode = useCallback(
    (texts: ExtractedText[], barcodes: string[]) => {
      return findBarcodeMatches(texts, barcodes);
    },
    []
  );

  return {
    // Core functions
    captureAndExtract,
    extractText,

    // Offline queue
    queueOCRRequest,
    syncOCRQueue,
    getQueuedOCRCount,

    // State
    uiState,
    resetState,
    getLastBestMatch,

    // Utilities
    matchTextsToBarcode,

    // Config
    config: ocrConfig,
  };
};

export default useOCR;
